import React from 'react'
import {ShareType} from 'components/Accommodation/ShareButton/constants'
import {createHotelShareUrl, SearchProps} from 'utils/sharing'

import {useToastController} from '@vio/atlas/context/toastController'
import {Icon} from '@vio/atlas/Icon'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {logAndCaptureErrorMessage} from '@vio/core/src/utils/logging/errorHandlers'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

interface Params {
  shareParams: SearchProps
  component?: string
  shareType?: ShareType
}

const isAbortError = (error: unknown) =>
  (error as {name?: string} | undefined)?.name === 'AbortError'

export const useShare = ({shareParams, component, shareType}: Params) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const toast = useToastController()

  const isHydrated = useIsHydrated()
  const shareIsAvailable = Boolean(isHydrated && navigator.share)
  const isDevelopmentMode = process.env.NODE_ENV === 'development'

  const shouldShow = () =>
    shareIsAvailable ||
    // Displaying it also in dev mode to make sure it's always visible in mobile-emulation for easier testing
    isDevelopmentMode

  const copyLink = async (url: string) => {
    await navigator.clipboard.writeText(url)
    toast.open(
      <FormattedMessageWrapper
        id="referralProgram.linkCopied"
        defaultMessage="Link copied"
      />,
      {
        icon: <Icon name="Checkmark" size="sm" />,
        closeOnOutsideClick: true,
        style: {width: 'fit-content', minWidth: 'auto'}
      }
    )
  }

  const onShare = async () => {
    const url = createHotelShareUrl({...shareParams, shareType})
    if (!url) return

    const method = navigator.share ? 'share' : 'copy'

    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.ShareLinkButton,
        action: Action.Clicked,
        component,
        payload: {method}
      })
    )

    try {
      if (method === 'share') {
        // Uses postMessage to webview if it's in the app
        await navigator.share({url})
        return
      }

      await copyLink(url)
    } catch (error) {
      if (isAbortError(error)) return
      logAndCaptureErrorMessage(error)
      if (method === 'share') {
        await copyLink(url).catch(logAndCaptureErrorMessage)
      }
    }
  }

  return {
    onShare,
    shouldShow
  }
}
